import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NotificationCenterComponent as CommonDomainNotificationCenterComponent,
  NotificationCenterInboxFacadeService,
  type NotificationCenterItem,
} from '@celerofinancas/common-domain';
import { CompanyState, UserState, type UserStateModel } from '@celerofinancas/state-management';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, map } from 'rxjs';

/**
 * Shared Notification Center widget integrated with the real inbox facade.
 */
@Component({
  selector: 'caixa-webcomponent-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  standalone: true,
  imports: [CommonDomainNotificationCenterComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationCenterComponent {
  /**
   * Event emitted when the user clicks a notification with a non-null redirectUri, mirroring the shell's redirect intent.
   */
  @Output()
  public redirectIntent = new EventEmitter<NotificationCenterItem>();

  /**
   * Shared inbox facade exposed by the common-domain package.
   */
  public readonly inboxFacade = inject(NotificationCenterInboxFacadeService);

  /**
   * Store reference used to resolve the active Caixa session.
   */
  private readonly store = inject(Store);

  /**
   * Host-controlled popover state mirrored from the trigger callbacks.
   */
  public readonly isOpen = signal(false);

  /**
   * Resolved user primary key used to connect the real inbox facade.
   */
  public readonly userPk = toSignal(
    this.store.select(UserState.getState).pipe(
      map((userState: UserStateModel | null) => userState?.pk ?? null),
      distinctUntilChanged(),
    ),
    { initialValue: null },
  );

  /**
   * Resolved company primary key used to connect the real inbox facade.
   */
  public readonly companyPk = toSignal(
    this.store.select(CompanyState.getActiveCompanyPk).pipe(distinctUntilChanged()),
    { initialValue: null },
  );

  /**
   * Canonical page state exposed by the real inbox facade.
   */
  public readonly page = this.inboxFacade.page;

  /**
   * Shared unread counter exposed by the real inbox facade.
   */
  public readonly unreadCount = this.inboxFacade.unreadCount;

  /**
   * Shared loading state composed from page loading and optimistic mutations.
   */
  public readonly isLoading = computed(
    () => this.inboxFacade.isLoading() || this.inboxFacade.isMutating(),
  );

  /**
   * Shared loading more state exposed by the real inbox facade.
   */
  public readonly isLoadingMore = computed(() => this.inboxFacade.isLoadingMore());

  /**
   * Reactively syncs the current Caixa session with the inbox facade.
   */
  private readonly syncNotificationContext = effect(
    () => {
      const userPk = this.userPk();
      const companyPk = this.companyPk();
      const resolvedSessionKey = userPk && companyPk ? `${userPk}:${companyPk}` : null;

      if (!resolvedSessionKey) {
        return;
      }

      this.isOpen.set(false);
      this.inboxFacade.connect({
        companyPk,
        now: new Date(),
        userPk,
      });
      // TODO: Migrate to handleOpenRequested in future to avoid unnecessary requests
      void this.inboxFacade.loadInitial();
    },
    { allowSignalWrites: true },
  );

  /**
   * Opens the shared Notification Center popover.
   */
  public handleOpenRequested(): void {
    this.isOpen.set(true);
  }

  /**
   * Closes the shared Notification Center popover.
   */
  public handleCloseRequested(): void {
    this.isOpen.set(false);
  }

  /**
   * Reloads the first page from the real inbox facade.
   */
  public handleReloadRequested(): void {
    void this.inboxFacade.loadInitial();
  }

  /**
   * Opens the notification's external link in a new tab (same behaviour as the
   * header "Conectar Contas" action) and mirrors the redirect event outward so
   * the host can still observe emitted navigation intents.
   * @param item Notification item emitted by the shell
   */
  public handleRedirectIntent(item: NotificationCenterItem): void {
    this.redirectIntent.emit(item);

    if (item.redirectUri) {
      window.open(item.redirectUri, '_blank');
    }

    this.isOpen.set(false);
  }
}
